import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { BraintreeInstances, VenmoPaymentResponse } from '@/types/venmo';
import { VENMO_ERRORS } from '@/constants/venmo';
import { CheckoutContext } from '@/context/CheckoutContext';
import { braintreeService } from '@/services/braintreeService';
import { useTranslate } from '@/hooks/useTranslate';
import { getVenmoTokenizeErrorMessage } from '@/utils/venmo';

interface UseVenmoBraintreeParams {
  braintreePK?: string;
  onPaymentError?: (error: string) => void;
}

/**
 * Grace period after the shopper returns from the Venmo app/tab before the
 * watchdog releases a stuck "in progress" Pay button. Long enough for a
 * genuine just-approved tokenize to resolve and take over; short enough that
 * a cancel-by-closing feels responsive.
 */
const VENMO_RETURN_GRACE_MS = 2500;

export const useVenmoBraintree = ({ braintreePK, onPaymentError }: UseVenmoBraintreeParams) => {
  const [instances, setInstances] = useState<BraintreeInstances>({
    venmo: null,
    dataCollector: null,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const isPaymentInProgress = useRef(false);
  const initializedPKRef = useRef<string | null>(null);

  const {
    payment: { isInPaymentProcess },
    actions: { runPayment, setError, setIsInPaymentProcess },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  // setError is intentionally omitted from deps: the wrapper in
  // CheckoutContent is rebuilt every parent render, which would re-create
  // initializeBraintree and re-fire the effect below. The underlying React
  // setState setter is stable, so the captured reference stays correct.
  const initializeBraintree = useCallback(
    async (publicKey: string) => {
      setIsInitializing(true);
      try {
        const [venmo, dataCollector] = await Promise.all([
          braintreeService.createVenmoClient(publicKey),
          braintreeService.createBraintreeDataCollector(publicKey),
        ]);
        setInstances({ venmo, dataCollector });
      } catch {
        setError(VENMO_ERRORS.INITIALIZATION_FAILED);
        setInstances({ venmo: null, dataCollector: null });
      } finally {
        setIsInitializing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!braintreePK || initializedPKRef.current === braintreePK) {
      return;
    }
    initializedPKRef.current = braintreePK;
    initializeBraintree(braintreePK);
  }, [braintreePK, initializeBraintree]);

  const processPayment = useCallback(
    async (nonce: string) => {
      await runPayment<VenmoPaymentResponse>({
        nonce,
        deviceData: instances.dataCollector?.deviceData,
      });
    },
    [instances.dataCollector, runPayment]
  );

  const handleSubmit = useCallback(async () => {
    if (isPaymentInProgress.current || isInPaymentProcess) {
      return;
    }

    if (!instances.venmo) {
      setError(t('paymentMethodNotAvailable'));
      return;
    }

    const isSupported = instances.venmo.isBrowserSupported();

    if (!isSupported) {
      setError(t('paymentMethodBrowserNotSupported'));
      return;
    }

    setError('');
    setIsInPaymentProcess(true);
    isPaymentInProgress.current = true;

    // Braintree is pinned to 3.118.0 (see constants/braintree.ts), which has
    // NO auto-cancel when the shopper closes the Venmo tab/app without acting:
    // the payment context stays CREATED and `tokenize()` never settles, so the
    // Pay button would stay stuck "in progress" forever. Watch for the shopper
    // returning to our page (app-switch back) and, if `tokenize()` still hasn't
    // produced a nonce after a short grace, release the button. `tokenizeSettled`
    // guards against racing a late-but-successful tokenize → finalize.
    let tokenizeSettled = false;
    let sawHidden = false;
    let watchdogTimer: ReturnType<typeof setTimeout> | null = null;

    const onVisibilityChange = () => {
      if (document.hidden) {
        sawHidden = true;
        return;
      }
      // Only arm after a real app-switch round-trip (hidden -> visible) so the
      // in-page desktop QR modal flow (which never hides the page and rejects
      // tokenize on close) keeps relying on the `finally` reset below.
      if (!sawHidden || tokenizeSettled || !isPaymentInProgress.current) {
        return;
      }
      if (watchdogTimer) clearTimeout(watchdogTimer);
      watchdogTimer = setTimeout(() => {
        if (!tokenizeSettled && isPaymentInProgress.current) {
          isPaymentInProgress.current = false;
          setIsInPaymentProcess(false);
        }
      }, VENMO_RETURN_GRACE_MS);
    };

    const clearWatchdog = () => {
      if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    try {
      const payload = await instances.venmo.tokenize();
      tokenizeSettled = true;
      const nonce = payload.nonce;

      await processPayment(nonce);
    } catch (error) {
      tokenizeSettled = true;
      const braintreeError = error as { code?: string; message?: string };
      const userMessage = getVenmoTokenizeErrorMessage(braintreeError.code, t);

      if (userMessage) {
        setError(userMessage);
      }

      onPaymentError?.(userMessage);
    } finally {
      clearWatchdog();
      setIsInPaymentProcess(false);
      isPaymentInProgress.current = false;
    }
  }, [isInPaymentProcess, instances.venmo, processPayment, setError, onPaymentError]);

  return {
    isInitializing,
    isReady: !isInitializing && !!instances.venmo,
    handleSubmit,
  };
};
